import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { getUserInfo, setContextCompany } from 'apis/UserAPI';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleLogout, handleUpdateAccessToken, handleUpdateUser } from 'store/slices/authSlice';
import { handleSetContextCompany } from 'store/slices/globalSlice';

import Content from './Content/Content';
import RootLoaders from './RootLoaders';
import { USER_DATA_VALIDATION_FIELD } from '../../constants';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 100vh;
`;

interface Props {
  children: React.ReactNode;
}

const AppLayout: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { currentUser } = useAppSelector((state) => state.auth);
  const { selectedCommunity, selectedParentCommunity, contextCompany } = useAppSelector((state) => state.global);

  useQuery({
    queryKey: [location.pathname, currentUser],
    queryFn: getUserInfo,
    onSuccess: (userData) => {
      if (
        currentUser &&
        userData &&
        USER_DATA_VALIDATION_FIELD in currentUser &&
        USER_DATA_VALIDATION_FIELD in userData &&
        currentUser.hash != userData.hash
      ) {
        dispatch(handleLogout());
      } else {
        dispatch(handleUpdateUser(userData));
      }
    },
  });

  useEffect(() => {
    let active = true;

    const setupContextCompany = async () => {
      if (currentUser?.holding && selectedCommunity) {
        // `selectedCommunity` is persisted; older or partial payloads can be missing the nested company object at boot.
        const community_company_id = selectedParentCommunity?.company_id ?? selectedCommunity.company?.id;

        if (community_company_id) {
          return await setContextCompany(community_company_id as number);
        }

        return await setContextCompany();
      }
      if (currentUser?.holding) {
        return await setContextCompany(contextCompany?.id);
      }
      return await setContextCompany();
    };

    if (currentUser) {
      setupContextCompany().then((response) => {
        if (!active) {
          return;
        }

        if (response.accessToken) {
          dispatch(handleUpdateAccessToken(response.accessToken));
        }
        if ('company' in response) {
          dispatch(handleSetContextCompany(response.company));
        }
      });
    }

    return () => {
      active = false;
    };
  }, [
    currentUser?.user_id,
    currentUser?.company,
    currentUser?.holding,
    selectedCommunity,
    dispatch,
    selectedParentCommunity,
  ]);

  return (
    <Container>
      <RootLoaders>
        <Content>{children}</Content>
      </RootLoaders>
    </Container>
  );
};

export default AppLayout;
